import React, { useCallback, useContext, useEffect, useState } from "react";
import { useIntl, history } from "umi";
import BenchmarkMetricsTable from "./components/BenchmarkMetricsTable";
import BenchmarkQuestionList from "./components/BenchmarkQuestionList";
import SearchComm from "./components/SearchComm";
import { getExploreSelector, getMetricList } from "../../services/benchmark";
import { FRAME_TYPE_MAP } from "../../_config/constant";
import { buriedBtnPoint, buriedPoint } from "@home/utils/buriedPoint";
import { LayoutContext } from "../../state/LayoutContext";
import { getLogTypeId } from "../../utils/utils";
import BenchmarkGenreList from "./components/BenchmarkGenreList";
import SvgIcon from "@home/components/SvgIcon";
import './index.scss';
import classNames from "classnames";
import type {
  IExploreProps,
  IBenchmarkGenre,
  IBenchmarkSource,
  IPaging,
  ITableResult,
  IRowClickValue
} from './type';

const Explore: React.FC<IExploreProps> = () => {
  const { formatMessage } = useIntl(); // 中英切换方法
  const { frameType, currentMenu } = useContext(LayoutContext);
  // url存储参数
  const initialSearch = new URLSearchParams(window.location.search).get('search') || '';
  const initialGenre = new URLSearchParams(window.location.search).get('genre') || 'ALL';
  const initialCategory = new URLSearchParams(window.location.search).get('category') || 'ALL';
  const initialSource = new URLSearchParams(window.location.search).get('source');
  const initialSourceArray = initialSource ? initialSource?.split(',') : ['ALL'];
  const initialPaging = new URLSearchParams(window.location.search).get('pageIndex');
  const initialPageSize = new URLSearchParams(window.location.search).get('pageSize');
  // state
  const [genres, setGenres] = useState<string>(initialGenre);
  const [keyword, setKeyword] = useState<string>(initialSearch);
  const [category, setCategory] = useState<string>(initialCategory);
  const [source, setSource] = useState<string[]>(initialSourceArray);
  const [paging, setPaging] = useState<IPaging>({ pageIndex: Number(initialPaging), pageSize: Number(initialPageSize) });
  const [tableResult, setTableResult] = useState<ITableResult>({ list: [], page: 1, page_size: 10, row_count: 0 });
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableError, setTableError] = useState<boolean>(false);
  const [genresList, setGenresList] = useState<IBenchmarkGenre[]>([]);
  const [categoriesList, setCategoriesList] = useState<IBenchmarkGenre[]>([]);
  const [sourcesList, setSourceList] = useState<IBenchmarkSource[]>([]);
  const pageId = '800101';
  // url储存key
  const updateUrlParams = (newParams: { [key: string]: string | number | Array<string | number>; } = {}) => {
    const queryString = Object.entries(newParams)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}=${value.join(',')}`; // 手动拼接数组值
        }
        return `${key}=${value}`;
      })
      .join('&');
    const newUrl = `${history.location.pathname}?${queryString}`;
    history.replace(newUrl);
  };
  // 品类, 数据源, 分类tab接口调用
  const fetchSelector = useCallback(async () => {
    const payload = {
      frame_type: FRAME_TYPE_MAP[frameType || 'pcconsole'],
      selector_list: []
    };

    try {
      const res = await getExploreSelector(payload);
      const { genre_list: genreList, category_list: categoryList, source_list: sourceList } = res || {};
      setGenresList(genreList);
      setCategoriesList(categoryList);
      setSourceList(sourceList);
    } catch (e) {
      console.log('err', e);
      setGenresList([]);
      setCategoriesList([]);
      setSourceList([]);
    }
  }, [frameType]);

  // 宽表接口调用
  const fetchTableResults = useCallback(async (keyword = '', genres = 'ALL', category = 'ALL', source: string[] = [], paging: IPaging = { pageIndex: 1, pageSize: 10 }) => {
    setTableResult({ list: [], page: 1, page_size: 10, row_count: 0 });
    setTableLoading(true);
    setTableError(false);
    const payload = {
      page: paging.pageIndex,
      page_size: paging.pageSize,
      keyword,
      frame_type: FRAME_TYPE_MAP[frameType || 'pcconsole'],
      category: category === 'ALL' ? '' : category,
      sources: (Array.isArray(source) && source.length === 1 && source[0] === 'ALL') ? [] : source,
      genres: genres === 'ALL' ? [] : [genres],
      is_recommend: !keyword
    };

    try {
      const res = await getMetricList(payload);
      setTableResult(res);
      setPaging({ pageIndex: res.page, pageSize: res.page_size });
    } catch (e) {
      console.log('Error fetching data:', e);
      setTableError(true);
    } finally {
      setTableLoading(false);
    }
  }, [frameType]);

  // 接口调用、打点、设置state状态
  useEffect(() => {
    if (frameType && currentMenu) {
      // 打点信息
      const logObj = {
        pageId,
        pageName: 'explore',
        extInfo: 'explore',
        pagePath: `/benchmark/${frameType}/explore`,
        uidType: '', // 基准打点不包含uidType
        typeId: getLogTypeId(frameType),
        menuId: currentMenu?.id,
      };
      buriedPoint(logObj);
      setKeyword(initialSearch);
      setCategory(initialCategory);
      setGenres(initialGenre);
      setSource(initialSourceArray);
      fetchSelector();
      fetchTableResults(keyword, genres, category, source, { pageIndex: Number(initialPaging), pageSize: Number(initialPageSize) });
    }
  }, [frameType, currentMenu]);

  // 更新url地址
  useEffect(() => {
    const { pageIndex, pageSize } = paging;
    const paramsMap: { [key: string]: string | number | string[]; } = {};
    if (!(pageIndex === 1 && pageSize === 10) && !(pageIndex === 0 && pageSize === 0)) {
      paramsMap.pageIndex = pageIndex;
      paramsMap.pageSize = pageSize;
    }
    if (keyword) {
      paramsMap.search = keyword;
    }
    if (genres !== 'ALL') {
      paramsMap.genre = genres;
    }
    if (category !== 'ALL') {
      paramsMap.category = category;
    }
    // 检查 source 是否为空数组或者是 ['ALL']，如果都不是，才设置url
    if (Array.isArray(source) && source.length > 0 && !(source.length === 1 && source[0] === 'ALL')) {
      paramsMap.source = source;
    }
    updateUrlParams({ ...paramsMap });
  }, [keyword, category, paging, genres, source]);

  // tab栏reset
  const tabReset = () => {
    setCategory('ALL');
    setGenres('ALL');
    setSource(['ALL']);
    setPaging({
      pageIndex: 1,
      pageSize: 10
    });
    fetchTableResults(keyword);
  };

  return <>
    <div className={
      classNames(
        'dbb-explore',
        { 'dbb-explore-mobile': frameType === 'mobile' },
        { 'dbb-explore-pc-console': frameType === 'pcconsole' }
      )
    }>
      <div className="dbb-explore-header">
        <div className="dbb-explore-system">
          <div className="dbb-explore-system-line dbb-explore-system-line-left">
          </div>
          <div className="dbb-explore-system-name">
            { formatMessage({ id: frameType === 'pcconsole' ? 'dbb_explore_pc_console_system' : 'dbb_explore_mobile_system' }) }
          </div>
          <div className="dbb-explore-system-line dbb-explore-system-line-right">
          </div>
        </div>
        <h1 className="dbb-explore-title">
          {formatMessage({ id: 'dbb_explore_title' })}
        </h1>
        <p className="dbb-explore-title-explain">
          {formatMessage({ id: 'dbb_explore_title_explain' })}
        </p>
      </div>
      <div className="dbb-explore-search">
        {/* 返回按钮 */}
        {initialSearch && (
          <div
            className={'db-header-title dbb-explore-back-btn'}
            onClick={() => {
              setKeyword('');
              setCategory('ALL');
              setGenres('ALL');
              setSource(['ALL']);
              setPaging({
                pageIndex: 1,
                pageSize: 10
              });
              fetchTableResults('');
            }}
          >
            <span className='dbb-header-title-box' style={{ display: 'flex', alignItems: 'center' }}>
              {<SvgIcon className=' dbb-explore-back-icon' type='a-gerenxinxi1'></SvgIcon>}
              <span className={'dbb-explore-back-text'}>
                {'Back'}
              </span>
            </span>
          </div>
        )}
        {/* 搜索框 */}
        <SearchComm
          onItemClick={(value: string) => {
            setKeyword(value);
            setCategory('ALL');
            setGenres('ALL');
            setSource(['ALL']);
            setPaging({
              pageIndex: 1,
              pageSize: 10
            });
            fetchTableResults(value);
            buriedBtnPoint({
              buttonId: `${pageId}001`,
              extInfo: JSON.stringify({
                keyword: value
              }),
              pageId,
              uidType: '', // 基准打点不包含uidType
              typeId: getLogTypeId(frameType)
            });
          }}
          placeholder={formatMessage({ id: 'dbb_explore_search_placeholder' })}
          // 参考google搜索，搜索为空就直接返回
          onSearchClick={(value: string) => {
            // if (!value.trim()) {
            //   return;
            // }
            if (keyword !== value) {
              setKeyword(value);
              setCategory('ALL');
              setGenres('ALL');
              setSource(['ALL']);
              setPaging({
                pageIndex: 1,
                pageSize: 10
              });
              buriedBtnPoint({
                buttonId: `${pageId}001`,
                extInfo: JSON.stringify({
                  keyword: value
                }),
                pageId,
                uidType: '', // 基准打点不包含uidType
                typeId: getLogTypeId(frameType)
              });
              fetchTableResults(value);
            }
          }}
          frameType={frameType || 'pcconsole'}
        />
      </div>
      <div className="dbb-explore-body">
        {/* 宽表 */}
        <BenchmarkMetricsTable
          dataSource={tableResult}
          genreValue={genres}
          categoryValue={category}
          sourceValue={source}
          onRowClick={(val: IRowClickValue) => {
            buriedBtnPoint({
              buttonId: `${pageId}005`,
              extInfo: JSON.stringify({
                metric: val?.metric
              }),
              pageId,
              uidType: '', // 基准打点不包含uidType
              typeId: getLogTypeId(frameType)
            });

            // 页面跳转判断
            let linkPage;
            if (val?.value_type?.trim() === 'otdr') {
              linkPage = 'metricOtdr';
            } else if (val?.value_type?.trim() === 'otdr,tips') {
              linkPage = 'metricOtdr';
            } else if (val?.value_type?.trim() === 'tips') {
              linkPage = 'metricTips';
            } else if (val?.category?.trim() === 'IAA') {
              linkPage = 'metricIaa';
            } 
            else {
              linkPage = 'metricDetail';
            }
            const link = genres !== 'ALL' ? `/benchmark/${frameType}/explore/${linkPage}/${val?.metric}?genre_subgenre=${genres}` : `/benchmark/${frameType}/explore/${linkPage}/${val?.metric}`;
            history.push(link);
          }}
          onTabReset={() => {
            tabReset();
          }}
          onGenreChange={(val: string) => {
            setGenres(val);
            setPaging({
              pageIndex: 1,
              pageSize: 10
            });
            fetchTableResults(keyword, val, category, source);
            buriedBtnPoint({
              buttonId: `${pageId}002`,
              extInfo: JSON.stringify({
                genres: val
              }),
              pageId,
              uidType: '', // 基准打点不包含uidType
              typeId: getLogTypeId(frameType)
            });
          }}
          onCategoryChange={(val: string) => {
            setCategory(val);
            setPaging({
              pageIndex: 1,
              pageSize: 10
            });
            fetchTableResults(keyword, genres, val, source);
          }}
          onSourceChange={(val: string[]) => {
            const sourceVal = (Array.isArray(val) && val.length === 0) ? ['ALL'] : val;
            setSource(sourceVal);
            setPaging({
              pageIndex: 1,
              pageSize: 10
            });
            fetchTableResults(keyword, genres, category, val);
            buriedBtnPoint({
              buttonId: `${pageId}002`,
              extInfo: JSON.stringify({
                sources: val
              }),
              pageId,
              uidType: '', // 基准打点不包含uidType
              typeId: getLogTypeId(frameType)
            });
          }}
          onPagingChange={(val: Partial<IPaging>) => {
            const { pageIndex = 1, pageSize = 10 } = val;
            setPaging({ pageIndex, pageSize });
            fetchTableResults(keyword, genres, category, source, { pageIndex, pageSize });
          }}
          paging={paging}
          loading={tableLoading}
          error={tableError}
          genresList={genresList}
          sourcesList={sourcesList}
          categoriesList={categoriesList}
          frameType={frameType || 'pcconsole'}
        />
        <div className="dbb-explore-list-container">
          {/* 推荐品类 */}
          {!initialSearch && (
            <BenchmarkGenreList frameType={frameType || 'pcconsole'} />
          )}
          {/* 推荐问题 */}
          {!initialSearch && (
            <BenchmarkQuestionList frameType={frameType || 'pcconsole'} />
          )}
        </div>
      </div>
      {
        !initialSearch && (
          <div className="dbb-explore-footer">
            <p className="dbb-explore-footer-explain">
              {formatMessage({ id: 'dbb_explore_footer_explain' })}
            </p>
          </div>
        )
      }
    </div>
  </>;
};

export default Explore;